import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { useNavigate } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Plus } from '../../assets';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import {
  WorkflowsClient,
  AutomatedWorkflowDto,
  WorkflowTriggerDto,
  WorkflowActionDto,
} from '../../app/services/api/workflowsClient';
import { rulesEngineScenarios } from '../../mocks/data/rulesEngineScenarios';

const workflowsClient = new WorkflowsClient();

const AutomatedWorkflowsPage: React.FC = () => {
  const { t } = useTranslation('automatedWorkflows');
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);

  const canView = hasPermissionTo ? hasPermissionTo(['AutomatedWorkflowsView', 'ConfigurationAdmin']) : true;
  const canCreate = hasPermissionTo ? hasPermissionTo(['AutomatedWorkflowsCreate', 'ConfigurationAdmin']) : true;
  const canEdit = hasPermissionTo ? hasPermissionTo(['AutomatedWorkflowsEdit', 'ConfigurationAdmin']) : true;
  const canDelete = hasPermissionTo ? hasPermissionTo(['AutomatedWorkflowsDelete', 'ConfigurationAdmin']) : true;

  const [workflows, setWorkflows] = useState<AutomatedWorkflowDto[]>([]);
  const [triggers, setTriggers] = useState<WorkflowTriggerDto[]>([]);
  const [actions, setActions] = useState<WorkflowActionDto[]>([]);

  const [searchText, setSearchText] = useState('');
  const [searchActiveOnly, setSearchActiveOnly] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState<string>('');
  const [searchAction, setSearchAction] = useState<string>('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [deleteTarget, setDeleteTarget] = useState<AutomatedWorkflowDto | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!canView) return;
    loadData();
  }, [canView]);

  const loadData = async () => {
    try {
      const [workflowsData, triggersData, actionsData] = await Promise.all([
        workflowsClient.getWorkflows(),
        workflowsClient.getTriggers(),
        workflowsClient.getActions(),
      ]);
      setWorkflows(workflowsData);
      setTriggers(triggersData);
      setActions(actionsData);
    } catch {
      setWorkflows([]);
    }
  };

  const getTriggerName = (type: string) => {
    return triggers.find(t => t.type === type)?.name ?? type;
  };

  const getActionName = (type: string) => {
    return actions.find(a => a.type === type)?.name ?? type;
  };

  const filteredWorkflows = useMemo(() => {
    return workflows.filter(w => {
      if (searchActiveOnly && !w.isActive) return false;
      if (searchText.trim()) {
        const text = searchText.toLowerCase();
        if (!w.name.toLowerCase().includes(text) && !w.description.toLowerCase().includes(text)) return false;
      }
      if (searchTrigger && w.trigger !== searchTrigger) return false;
      if (searchAction && w.action.type !== searchAction) return false;
      return true;
    });
  }, [workflows, searchActiveOnly, searchText, searchTrigger, searchAction]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pagedWorkflows = useMemo(
    () => filteredWorkflows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredWorkflows, page, rowsPerPage],
  );

  const handleOpenCreate = () => {
    if (!canCreate) return;
    navigate('/automated-workflows/new');
  };

  const handleOpenEdit = (row: AutomatedWorkflowDto) => {
    if (!canEdit) return;
    navigate(`/automated-workflows/${row.id}`);
  };

  const handleRequestDelete = (row: AutomatedWorkflowDto, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canDelete) return;
    setDeleteTarget(row);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await workflowsClient.deleteWorkflow(deleteTarget.id);
      setWorkflows(prev => prev.filter(w => w.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteConfirmOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteTarget(null);
    setDeleteConfirmOpen(false);
  };

  const handleToggleActive = async (workflow: AutomatedWorkflowDto, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEdit) return;
    try {
      const updated = await workflowsClient.toggleWorkflowActive(workflow.id, !workflow.isActive);
      setWorkflows(prev => prev.map(w => w.id === updated.id ? updated : w));
    } catch {
      // Handle error
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const uniqueTriggerTypes = useMemo(() => {
    const types = new Set(workflows.map(w => w.trigger));
    return triggers.filter(t => types.has(t.type));
  }, [workflows, triggers]);

  const uniqueActionTypes = useMemo(() => {
    const types = new Set(workflows.map(w => w.action.type));
    return actions.filter(a => types.has(a.type));
  }, [workflows, actions]);

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>{t('pageTitle', 'Automated Workflows')}</PageTitle>
        <Pill text={t('noPermissionView', 'You do not have permission to view automated workflows.')} />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <PageTitle subTitle={t('pageSubtitle', 'Configure automated actions triggered by system events')}>
            {t('pageTitle', 'Automated Workflows')}
          </PageTitle>
        </Grid>

        <Grid item
          xs={12}
          md={9}>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}
              md={3}>
              <TextField
                label={t('searchLabel', 'Search')}
                fullWidth
                size='small'
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder={t('searchPlaceholder', 'Search by name or description')}
              />
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <TextField
                select
                label={t('triggerLabel', 'Trigger')}
                fullWidth
                size='small'
                value={searchTrigger}
                onChange={e => setSearchTrigger(e.target.value)}
              >
                <MenuItem value=''>{t('allTriggers', 'All Triggers')}</MenuItem>
                {uniqueTriggerTypes.map(t => (
                  <MenuItem key={t.type}
                    value={t.type}>{t.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <TextField
                select
                label={t('actionLabel', 'Action')}
                fullWidth
                size='small'
                value={searchAction}
                onChange={e => setSearchAction(e.target.value)}
              >
                <MenuItem value=''>{t('allActions', 'All Actions')}</MenuItem>
                {uniqueActionTypes.map(a => (
                  <MenuItem key={a.type}
                    value={a.type}>{a.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={searchActiveOnly}
                    onChange={e => setSearchActiveOnly(e.target.checked)}
                  />
                }
                label={t('activeOnly', 'Active only')}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}
          md={3}
          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
          {canCreate && (
            <Button
              size='lg'
              variant='primary'
              startIcon={<Plus />}
              onClick={handleOpenCreate}
            >
              {t('createWorkflow', 'Create Workflow')}
            </Button>
          )}
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>{t('pageTitle', 'Automated Workflows')}</TableHeader>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('columnName', 'Name')}</TableCell>
                    <TableCell>{t('triggerLabel', 'Trigger')}</TableCell>
                    <TableCell>{t('actionLabel', 'Action')}</TableCell>
                    <TableCell>{t('status', 'Status')}</TableCell>
                    <TableCell>{t('columnTriggerCount', 'Trigger Count')}</TableCell>
                    <TableCell>{t('columnLastTriggered', 'Last Triggered')}</TableCell>
                    <TableCell align='right'>{t('columnActions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedWorkflows.map(row => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleOpenEdit(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <div style={{ fontWeight: 500 }}>{row.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>{row.description}</div>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTriggerName(row.trigger)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getActionName(row.action.type)}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.isActive ? t('statusActive', 'Active') : t('statusInactive', 'Inactive')}
                          size="small"
                          color={row.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{row.triggerCount}</TableCell>
                      <TableCell>{formatDate(row.lastTriggeredAtUtc)}</TableCell>
                      <TableCell align='right'
                        onClick={e => e.stopPropagation()}>
                        <Tooltip title={row.isActive ? t('deactivateTooltip', 'Deactivate') : t('activateTooltip', 'Activate')}>
                          <IconButton
                            size="small"
                            onClick={e => handleToggleActive(row, e)}
                          >
                            {row.isActive ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        {canEdit && (
                          <Tooltip title={t('editTooltip', 'Edit')}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(row)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title={t('deleteTooltip', 'Delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={e => handleRequestDelete(row, e)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pagedWorkflows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}
                        style={{ textAlign: 'center' }}>
                        {t('noWorkflowsFound', 'No workflows found.')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      count={filteredWorkflows.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Section>
        </Grid>
      </Grid>

      <Grid container
        sx={{ mt: 8 }}>
        <Grid item
          xs={12}>
          <Section>
            <Box sx={{ p: 4 }}>
              <Typography variant="textMD"
                fontWeight={600}
                sx={{ mb: 2 }}>
                {t('catalogueTitle', 'Rule Scenario Catalogue')}
              </Typography>
              <Typography variant="textSM"
                color="text.secondary"
                sx={{ mb: 4 }}>
                {t('catalogueDescription', 'The underlying rules-engine scenarios this demo can narrate. "Live" scenarios are actually evaluated by the rules engine and surfaced as exception banners on their screen; the rest are seeded/narratable content only. This is a separate catalogue from the workflows above, which are illustrative configuration only.')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('scenarioColumn', 'Scenario')}</TableCell>
                      <TableCell>{t('categoryColumn', 'Category')}</TableCell>
                      <TableCell>{t('severityColumn', 'Severity')}</TableCell>
                      <TableCell>{t('status', 'Status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rulesEngineScenarios.map((scenario) => (
                      <TableRow key={scenario.code}>
                        <TableCell>
                          <Typography variant="textSM"
                            fontWeight={600}>{scenario.title}</Typography>
                          <Typography variant="textXS"
                            color="text.secondary">{scenario.description}</Typography>
                        </TableCell>
                        <TableCell>{scenario.category}</TableCell>
                        <TableCell>
                          <Chip label={scenario.severity}
                            size="small"
                            color={scenario.severity === 'Blocking' ? 'error' : scenario.severity === 'Warning' ? 'warning' : 'default'} />
                        </TableCell>
                        <TableCell>
                          <Chip label={scenario.demoWired ? t('scenarioLive', 'Live') : t('scenarioNarrated', 'Narrated')}
                            size="small"
                            color={scenario.demoWired ? 'success' : 'default'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Section>
        </Grid>
      </Grid>

      {deleteTarget && (
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
        >
          <DialogTitle>{t('deleteDialogTitle', 'Delete workflow?')}</DialogTitle>
          <DialogContent>
            {t('deleteDialogBody', 'Are you sure you want to delete the workflow "{{name}}"? This action cannot be undone.', { name: deleteTarget.name })}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}
              disabled={isDeleting}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              variant='primary'
              color='error'
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {t('delete', 'Delete')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </PageArea>
  );
};

export default AutomatedWorkflowsPage;
